<template>
  <div class="transponder">
    <HeaderParticipant 
        :participants="participants" 
        :participant="participant" 
        @selectParticipants="selectParticipants" 
        @changeSelect="changeSelect"
    />
    <v-row :class="{ 'mt-4': !age18 }">
      <v-col cols="12" md="12" sm="12">
        <v-dialog v-model="dialog" max-width="600px" >
          <v-card>
            <v-form ref="form">
              <v-card-title>
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>
              <v-card-text>
                  <v-row class="mt-2">
                    <v-col md="12">
                      <v-text-field
                        v-model="form.tagId"
                        :label="$t('transponder.add_transponder')"
                        :rules="rules.tagId"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col md="12" v-if="isManualTags">
                      <p>{{ $t('transponder.add_one_time_tags') }}</p>
                      <template v-for="(event, i) in eventsForUser">
                        <v-checkbox
                           :key="`check-${event.id}`"
                          :label="event.eventName"
                          v-model="events[userIdSelected][i].selected"
                          dense
                        ></v-checkbox> 
                        <br  :key="`br-${event.id}`">
                      </template>
                    </v-col>
                  </v-row>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  @click="dialog = false"
                  :color="`${buttonColor}`"
                  class="btn mb-2 mr-2"
                >{{ $t('transponder.cancel') }}</v-btn>
                <v-btn
                  @click="createTransponder"
                  :color="`${buttonColor}`"
                  class="btn mb-2"
                  :disabled="!transponderFormIsValid"
                >{{ $t('transponder.save') }}</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="600px">
          <v-card>
            <v-card-title class="headline">{{ $t('transponder.delete_transponder') }}</v-card-title>
            <v-card-text>
              <p class="mt16">{{ $t('transponder.sure_delete') }}</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                :color="`${buttonColor}`"
                class="btn mb-2 mr-2"
                @click="closeDelete"
              >{{ $t('transponder.cancel') }}</v-btn>
              <v-btn
                :color="`${buttonColor}`"
                class="btn mb-2"
                @click="deleteItemConfirm"
              >{{ $t('transponder.delete') }}</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <div v-for="part in participantList" :key="part.id" class="ml-2 mr-2 mb-2">
          <v-card class="elevation-4" :style="`background:${cardBGColor}`">
            <v-card-text>
              <v-row no-gutters>
                <v-col md="6" class="text-left" style="position:relative">
                  <HeaderCard :part="part" />
                </v-col>
                <v-col md="6" class="text-right">
                  <v-btn
                    :color="`${buttonColor}`"
                    dark
                    @click="openDialogCreate(part.userId)"
                  >{{ $t("transponder.add_transponder") }}</v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col md="12">
                   <v-data-table
                    :headers="transponderHeaders"
                    :items="part.tags"
                    sort-by="year"
                    class="elevation-1"
                    :no-data-text="$t('transponder.no_data')"
                    dense
                    :footer-props="{
                      'items-per-page-text': $t('dataTable.rowsPerPageText'),
                      'items-per-page-all-text': $t('dataTable.rowsPerPageAllText')
                    }"
                    :headerProps="{
                      'sort-by-text': $t('dataTable.sortByText')
                    }"
                  >
                    <template v-slot:[`item.tagId`]="{ item }">
                      {{ 
                          item.isManual ? item.tagId : 
                          `${item.tagId.substring(0,3)}-${item.tagId.substring(3,6)}-${item.tagId.substring(item.tagId.length - 3)}` 
                      }}
                    </template>
                    <template v-slot:[`item.createdDate`]="{ item }">
                      {{ item.createdDate | formatDateForHumans }}
                    </template>
                    <template v-slot:[`item.lastUsed`]="{ item }">
                      {{ item.createdDate | formatDateForHumans }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-icon small class="mr-2" v-if="item.isManual" @click="editItem(item)">fa far fa-pencil-alt</v-icon>
                      <v-icon small @click="deleteItem(item)">fa far fa-trash-alt</v-icon>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>
      </v-col>
    </v-row>
    <Snack :snack="snackbar" :snackMsg="msg_snackbar" :snackCol="color_snackbar"/>
  </div>
</template>

<script>
import Snack from "../components/notifications/Snack";
import AuthService from "../services/AuthService";
import TagService from "../services/TagService";
import moment from "moment";
import { VCard, VCol, VRow, VBtn, VIcon, VTextField, VCardText, VForm, VDialog, VDataTable, 
         VCardTitle, VCardActions, VSpacer, VCheckbox } from 'vuetify/lib';
import GeneralService from "@/services/GeneralService";
import HeaderCard from "@/components/HeaderCard";
import { mapState } from "vuex";
import HeaderParticipant from "@/components/HeaderParticipant";

export default {
  name: "Transponder",
  components:{
    Snack,
    VCard, VCol, VRow, VBtn, VIcon, VTextField, VCardText, VForm, VDialog, VDataTable, 
    VCardTitle, VCardActions, VSpacer, VCheckbox,
    HeaderCard, HeaderParticipant
  },
  data() {
    const transponderForm = Object.freeze({
      tagId: "",
      isManual: 1,
      userId: 0
    });
    return {
      color_snackbar: "",
      msg_snackbar: "",
      snackbar: false,
      dialog: false,
      editedIndex: -1,
      dialogDelete: false,
      transponderForm,
      form: Object.assign({}, transponderForm),
      transponderItems: [],
      defaultItem: {
        tagid: "",
        created_date: "01/12/2020",
        date_last_used: "01/12/2020"
      },
      userId: "",
      buttonColor: "#000",
      cardBGColor: "#f1f1f1",
      bandLoadingParticipants: false,
      participantList: [],
      events: [],
      userIdSelected: 0
    };
  },
  mounted() {
    this.$emit("titlePage", "transponder.title");
    this.$emit("dynamicMenuItems", this.dynamicMenuItems);
    this.getParticipants();
  },
  computed: {
    isManualTags() {
      return this.eventsForUser.length > 0;
    },
    eventsForUser() {
      return (this.events[this.userIdSelected] || []);
    },
    transponderFormIsValid() {
      return this.form.tagId;
    },
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("transponder.add_transponder")
        : this.$t("transponder.update_transponder");
    },
    btnTitle() {
      return this.editedIndex === -1
        ? this.$t("transponder.create")
        : this.$t("transponder.update");
    },
    transponderHeaders() {
      return [
        {
          text: this.$t("transponder.tag_id"),
          align: "",
          sortable: false,
          value: "tagId"
        },
        { text: this.$t("transponder.created_date"), value: "createdDate" },
        {
          text: this.$t("transponder.date_last_used"),
          value: "lastUsed"
        },
        {
          text: this.$t("transponder.actions"),
          value: "actions",
          sortable: false
        }
      ];
    },
    ...mapState({
      content2(state) {
        return this.$t("common.selectParticipants.content_2", { livelapsId: state.session.userid })
      },
      age18: (state) => state.session.age18,
      userStore: (state) => state.session.user,
      participants: (state) => state.session.participants,
      participant: (state) => state.session.participantsSelected
    }),
    rules() {
      return {
      tagId: [val => (val || "").length > 0 || this.$t("transponder.tagid")]
      };
    }
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    userStore(val) {
      if(val) {
        this.getParticipants();
      }
    },
    participant() {
      if(!this.bandLoadingPartSelected) {
        this.bandLoadingPartSelected = true;
        this.selectParticipants();
      }
    }
  },
  methods: {
    loadImage(url) {
        const urlImage = GeneralService.cleanImage(url, this.$store.getters.env.get('VUE_APP_IMAGE_PLACEHOLDER'));
        return urlImage;
    },
    selectParticipants(){ 
      this.participantList = this.participantList.filter((el) => {
        const find = this.participant.find((ele) => { 
          return ele.userId ? el.userId == ele.userId : el.id == ele.id
        });
        return find ? true : false;
      });
      const usersId = this.participant.map((el) => { return el.userId; });
      this.getTagsUsers(usersId);
      this.getEvents(usersId);
      AuthService.setAuthParticipants(this.userStore.id, this.participant); 
    },
    async getParticipants(){
      if(!this.bandLoadingParticipants) {
        if(this.userStore) {
          this.bandLoadingParticipants = true;
          this.$store.dispatch('setParticipantsAction');  
        } else {
          this.$store.dispatch('setUserAction');
        }
      }
    },
    showSnackBar(msg, color){
      this.snackbar = true;
      this.msg_snackbar = msg;
      this.color_snackbar = color;
      setTimeout(() => this.snackbar = false, 3000);
    },
    async getTags(userId) {
      let tags = [];
      TagService.getAll(userId).then(response => {
        if (response.data.success == 1) {
          response.data.message.map(function(value) {
            tags.push({
              createdDate: value.createdDate,
              id: value.id,
              isManual: value.isManual,
              lastUsed: value.lastUsed,
              tagId: value.tagId,
              userId: value.userId
            });
          });
        }
      });
      this.transponderItems = tags;
    },
    getTagsUsers(usersId) {
      TagService.getTagsUsers({ usersId }).then(response => {
        if (response.data.success == 1) {
          this.participantList = this.participant.map((el) => {
            const filter = response.data.message.filter((el1) => el.userId == el1.userId);
            return {
              ...el, 
              tags: filter.map((value) => {
                return {
                  createdDate: value.createdDate,
                  id: value.id,
                  isManual: value.isManual,
                  lastUsed: value.lastUsed,
                  tagId: value.tagId,
                  userId: value.userId,
                  events: value.events
                };
              })
            }
          });
        }
      });
    },
    setTag(data) { 
      const selectedEvents = this.eventsForUser.filter(event => event.selected).map(event => event.id);
      if (selectedEvents.length > 0) {
        data.events = selectedEvents;
      }
      TagService.create(data).then(response => {
        if (response.data.success == 1) {
          this.transponderItems = [];
          this.selectParticipants();
          this.showSnackBar(this.$t("transponder.msg_success_created"), "success");
        }else{
          if(typeof response.data.message === 'undefined') {
            this.showSnackBar(this.$t("transponder.error_edit_user"), "error");
          }else{
            const error = Array.isArray(response.data.message) ? response.data.message[0] : response.data.message;
            const errorMessage = this.$te(`transponder.${error}`) ? this.$t(`transponder.${error}`) : this.$t('common.error_general');
            this.showSnackBar(errorMessage, "error");
          }
        }
      });
    },
    deleteTag(id) {
      TagService.delete(this.form.userId, id).then(response => {
        if (response.data.success == 1) {
          this.transponderItems = [];
          this.selectParticipants();
          this.showSnackBar(this.$t("transponder.msg_success_deleted"), "success");
        }else{
          this.showSnackBar(this.$t("transponder.error_edit_user"), "error");
        }
      });
    },
    editTag(data) {
      const selectedEvents = this.eventsForUser.filter(event => event.selected).map(event => event.id);
      const events = selectedEvents.length > 0 ? selectedEvents : undefined;
      TagService.update({...data, events}).then(response => {
        if (response.data.success == 1) {
          this.transponderItems = [];
          if(response.data.message.isManual && response.data.message.isManual == 0) {
            this.showSnackBar(this.$t("transponder.is_not_manual"), "error");
          } else {
            this.selectParticipants();
            this.showSnackBar(this.$t("transponder.msg_success_edited"), "success");
          }
        }else{
          if(typeof response.data.message.error === 'undefined') {
            this.showSnackBar(this.$t("transponder.error_edit_user"), "error");
          }else{
            const error = Array.isArray(response.data.message.error) ? response.data.message.error[0] : response.data.message.error;
            const errorMessage = this.$te(`transponder.${error}`) ? this.$t(`transponder.${error}`) : this.$t('common.error_general');
            this.showSnackBar(errorMessage, "error");
          }
        }
      });
    },
    editItem(item) {
      this.editedIndex = item.id;
      this.userIdSelected = item.userId;
      if (this.eventsForUser.length && item.events.length) {
        // Reset all event selections
        this.events[this.userIdSelected].forEach(event => event.selected = false);

        // Create lookup map of valid events
        const validEvents = new Set(
          item.events
            .filter(event => event.tagId === item.tagId)
            .map(event => event.id)
        );

        // Mark matching events as selected
        this.events[this.userIdSelected].forEach(event => {
          event.selected = validEvents.has(event.id);
        });
      }
      this.form = Object.assign({}, item);
      this.dialog = true;
    },
    deleteItem(item) {
      this.editedIndex = item.id;
      this.form = Object.assign({}, item);
      this.dialogDelete = true;
    },
    deleteItemConfirm() {
      this.deleteTag(this.form.id);
      this.closeDelete();
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.form = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.form = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    createTransponder() {
      if (this.editedIndex > -1) {
        this.editTag(this.form);
      } else {
        this.form.createdDate = moment().format(
          "yyyy-MM-DD HH:mm:ss"
        );
        this.form.lastUsed = moment().format("yyyy-MM-DD HH:mm:ss");
        this.form.isManual = 1;
        this.setTag(this.form);
      }
      this.close();
    },
    openDialogCreate(userId){
      this.form.userId = userId;
      if(this.$refs.form) {
        this.$refs.form.resetValidation();
      }  
      this.userIdSelected = userId;
      this.dialog = true;
    },
    changeSelect(selectParts) {
      this.$store.commit('setParticipantsSelected', selectParts);
    },
    getEvents(usersId) {
      TagService.getEventsWithManualTags({ userIds: usersId }).then(response => {
        if (response.data.success == 1) { 
          this.events = [];
          Object.entries(response.data.message).forEach(([key, value]) => {
            this.events[key] = value.map(event => ({
              ...event,
              selected: false
            }));
          });
        }
      });
    }
  },
  created(){
    this.buttonColor = this.$store.getters.env.get('VUE_APP_BUTTON_COLOR');
    this.cardBGColor = this.$store.getters.env.get('VUE_APP_BACKGROUND_CARD_PART');
    this.borderAlertColor = this.$store.getters.env.get('VUE_APP_BORDER_ALERT_REGULAR');
    this.iconAlertColor = this.$store.getters.env.get('VUE_APP_ICON_ALERT_REGULAR');
    this.bgAlertColor = this.$store.getters.env.get('VUE_APP_BG_ALERT_REGULAR');
  }
};
</script>

<style>
.transponder .v-card__title {
  background: #ccc;
}
.transponder .v-data-table-header {
    background: #ccc;
}
</style>
<style scoped>
.mt16 {
  margin-top: 16px;
}
.btn{
  color:#fff !important;
}
.partImg{
  width: 40px;
  border-radius: 50%;
  height: 40px;
}
.partSpan{
  color: #000;
  position: absolute;
  top: 10px;
  left: 60px;
}
.v-card__actions{
  display: inherit;
  text-align: right;
}
</style>
